import React, { useRef } from 'react';
import { Button, Popconfirm, Select, Tag, Tooltip } from 'antd';
import { Copy, RotateCcw, Trash2, X } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import type { TaskStore } from '../../../stores/kanban/TaskStore';
import type { KanbanTask } from '../../../stores/KanbanTaskStores';
import { getPriorityColor, getPriorityIcon, getPriorityText } from '../useTaskHelpers';
import { useTaskField } from './useTaskField';

export type TaskHeaderProps = {
  task: KanbanTask;
  store: TaskStore;
  readOnly: boolean;
  isAnonymous: boolean;
  /** True while the deleted-tasks view is open: delete becomes permanent and restore appears. */
  isDeleted: boolean;
  onClose: () => void;
  onCopyLink: (task: KanbanTask) => void;
  onDelete?: (taskId: string) => Promise<void>;
  onRestore?: (taskId: string) => Promise<void>;
  onDeletePermanent?: (taskId: string) => Promise<void>;
};

/**
 * Sticky top bar: what the task is (key, priority, status) on the left, what can be done to it on
 * the right. The status select is duplicated in the sidebar on purpose — this one is the one-click
 * control that stays visible while the main column scrolls.
 */
const TaskHeader: React.FC<TaskHeaderProps> = observer(
  ({
    task,
    store,
    readOnly,
    isAnonymous,
    isDeleted,
    onClose,
    onCopyLink,
    onDelete,
    onRestore,
    onDeletePermanent,
  }) => {
    const { t } = useTranslation();
    const busy = useRef(false);

    const status = useTaskField<string>({
      identity: task.id,
      value: task.statusId,
      save: (statusId) => store.updateTaskStatus(task.id, statusId),
      successMessage: t('TaskStatusUpdated'),
      errorMessage: t('FailedToUpdateStatus'),
    });

    const canEdit = !readOnly && !isDeleted;
    const canDelete = !isAnonymous && !readOnly;
    const removeTask = isDeleted ? onDeletePermanent : onDelete;

    const runOnce = async (action?: (taskId: string) => Promise<void>) => {
      if (!action || busy.current) return;
      busy.current = true;
      try {
        await action(task.id);
      } finally {
        busy.current = false;
      }
    };

    return (
      <header className="task-drawer__header">
        <div className="task-drawer__header-left">
          {task.publicId ? <span className="task-drawer__key">{task.publicId.toUpperCase()}</span> : null}

          <Tag
            color={getPriorityColor(task.priority)}
            className="task-drawer__chip"
            icon={<span className="task-drawer__chip-icon">{getPriorityIcon(task.priority)}</span>}
          >
            {getPriorityText(task.priority, t)}
          </Tag>

          <Select
            className="task-drawer__status"
            size="small"
            value={status.draft}
            disabled={!canEdit}
            loading={status.saving}
            onChange={(value) => void status.commit(value)}
            options={store.projectStatuses.map((item) => ({ value: item.id, label: item.name }))}
          />

          {readOnly ? <Tag color="warning">{t('ReadOnly')}</Tag> : null}
        </div>

        <div className="task-drawer__header-actions">
          <Button icon={<Copy size={15} />} onClick={() => onCopyLink(task)}>
            {t('CopyLink')}
          </Button>

          {isDeleted && onRestore ? (
            <Button type="primary" icon={<RotateCcw size={15} />} onClick={() => void runOnce(onRestore)}>
              {t('Restore')}
            </Button>
          ) : null}

          {canDelete && removeTask ? (
            <Popconfirm
              title={t('ConfirmDeleteTask')}
              description={isDeleted ? t('AreYouSureDeleteTaskPermanent') : t('AreYouSureDeleteTask')}
              okText={t('Delete')}
              okType="danger"
              cancelText={t('Cancel')}
              placement="bottomRight"
              onConfirm={() => void runOnce(removeTask)}
            >
              <Button danger icon={<Trash2 size={15} />}>
                {t('Delete')}
              </Button>
            </Popconfirm>
          ) : null}

          <Tooltip title={t('taskDrawerClose')}>
            <Button
              type="text"
              aria-label={t('taskDrawerClose')}
              icon={<X size={18} />}
              onClick={onClose}
            />
          </Tooltip>
        </div>
      </header>
    );
  },
);

export default TaskHeader;
